import type { ChainId, StoreDebtEventId, StoreDebtId, StoreId } from "@KassaroAB/shared/types/ids";
import type {
  RestCreateStoreDebtSettlement,
  RestStoreDebt,
  RestStoreDebtEvent,
} from "@KassaroAB/shared/types/storeDebt";
import { api, ApiError } from "../utils/api";
import { auth } from "../config/firebase";
import { API_BASE_URL } from "../constants/api";

export const getStoreDebtsApi = async (
  chainId: ChainId,
  storeId?: StoreId,
): Promise<RestStoreDebt[]> => {
  const params = new URLSearchParams();
  if (storeId) {
    params.set("storeId", storeId);
  }
  const query = params.toString();
  return api.get(`chains/${chainId}/storeDebts${query ? `?${query}` : ""}`);
};

export const getStoreDebtApi = async (
  chainId: ChainId,
  storeDebtId: StoreDebtId,
): Promise<RestStoreDebt> => {
  return api.get(`chains/${chainId}/storeDebts/${storeDebtId}`);
};

export const getStoreDebtEventsApi = async (
  chainId: ChainId,
  storeDebtId: StoreDebtId,
  cursor: StoreDebtEventId | null,
): Promise<RestStoreDebtEvent[]> => {
  const params = new URLSearchParams();
  if (cursor) {
    params.set("cursor", cursor);
  }
  return api.get(
    `chains/${chainId}/storeDebts/${storeDebtId}/events?${params.toString()}`,
  );
};

export const createStoreDebtSettlementApi = async (
  chainId: ChainId,
  storeDebtId: StoreDebtId,
  body: RestCreateStoreDebtSettlement,
): Promise<{ storeDebt: RestStoreDebt; event: RestStoreDebtEvent }> => {
  return api.post(
    `chains/${chainId}/storeDebts/${storeDebtId}/settlements`,
    body,
  );
};

export const downloadStoreDebtReportPdfApi = async (
  chainId: ChainId,
  storeDebtId: StoreDebtId,
  startDate?: string,
  endDate?: string,
) => {
  if (!auth.currentUser) {
    throw new Error("User not authenticated");
  }
  const token = await auth.currentUser.getIdToken();
  const params = new URLSearchParams();
  if (startDate) {
    params.set("startDate", startDate);
  }
  if (endDate) {
    params.set("endDate", endDate);
  }
  const query = params.toString();
  const response = await fetch(
    `${API_BASE_URL}/chains/${chainId}/storeDebts/${storeDebtId}/report${query ? `?${query}` : ""}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new ApiError(response.status, data);
  }
  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition");
  const filenameMatch = disposition?.match(/filename="([^"]+)"/);
  const filename = filenameMatch?.[1] ?? "store-debt-report.pdf";
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
